<template>
  <div class="sidebar">
    <div class="h-full flex flex-col min-h-0">
      <component :is="isDeactivated ? 'div' : 'router-link'"
                 exact
                 to="/"
                 class="amio-logo"
      >
        <img :src="amioLogo" alt="amio logo" class="h-12">
      </component>

      <div v-bar class="flex-1 min-h-0 overflow-y-auto">
        <div class="min-h-full flex flex-col items-center justify-between">
          <!-- Top group: Answers -> History -->
          <div>
            <component v-for="({ name, icon }, i) in navigationItems"
                       :is="isDeactivated ? 'div' : 'vx-tooltip'"
                       :key="i"
                       position="right"
                       :text="resolveTooltipText(name)"
                       :enabled="isDisabled(name)"
            >
              <component :key="name"
                         :is="isBotLoading || isDisabled(name)  ? 'div' : 'router-link'"
                         ref="sidebarItem"
                         :class="linkClasses(i, name)"
                         :to="resolveNavTo(name)"
                         active-class="router-link-active"
                         class="sidebar-item"
              >
                <component :is="icon"
                           :class="{ 'opacity-50': selectedItemIndex !== i }"
                           :is-filled="selectedItemIndex === i"
                />
                {{ name }}
              </component>
            </component>
          </div>

          <!-- Bottom group: Support + Settings -->
          <div>
            <button type="button"
                    ref="supportButton"
                    class="sidebar-item border-solid border-0 border-t border-soft cursor-pointer bg-transparent p-0 font-inherit"
                    data-testid="support-button"
                    @click.stop="toggleSupportMenu"
            >
              <span class="support-icon">
                <support-icon class="opacity-50"/>
                <span v-if="supportUnread"
                      class="support-unread-dot"
                      data-testid="support-unread-dot"
                />
              </span>
              Support
            </button>

            <div v-if="isAdmin">
              <component :is="isDeactivated ? 'div' : 'router-link'"
                         class="sidebar-item border-solid border-0 border-t border-soft"
                         :to="{ name: ROUTE_NAMES.admin }"
              >
                <admin-icon class="opacity-50"/>
                Admin
              </component>
            </div>

            <div>
              <component :is="isBotLoading || isDeactivated ? 'div' : 'router-link'"
                         :class="settingsLinkClasses()"
                         :to="{ name: ROUTE_NAMES.settings, params: { botId } }"
                         active-class="router-link-active"
                         class="sidebar-item border-solid border-0 border-t border-soft"
              >
                <settings-icon :class="{ 'opacity-50': !$route.path.includes(ROUTE_NAMES.settings) }"
                               :is-filled="$route.path.includes(ROUTE_NAMES.settings)"
                />
                {{ ROUTE_NAMES.settings }}
              </component>
            </div>

            <router-link v-if="isDeactivated"
                         class="sidebar-item border-solid border-0 border-t border-soft"
                         data-testid="sidebar-logout"
                         :to="{ name: ROUTE_NAMES.logout }"
            >
              <logout-icon class="opacity-50"/>
              Logout
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isSupportMenuOpen"
         class="support-menu"
         data-testid="support-menu"
         :style="supportMenuStyle"
         @click.stop
    >
      <button type="button"
              class="support-menu-item"
              data-testid="support-chat-button"
              @click="openChat"
      >
        <span class="support-menu-label">
          Chat
          <span v-if="supportUnread"
                class="support-unread-dot"
                data-testid="support-chat-unread-dot"
          />
        </span>
      </button>
      <button type="button"
              class="support-menu-item"
              data-testid="report-bug-button"
              @click="openBugReport"
      >
        Report a bug
      </button>
    </div>

    <bug-report-dialog :is-shown="isBugReportShown"
                       @cancel="isBugReportShown = false"
                       @submitted="isBugReportShown = false"
    />
  </div>
</template>

<script>
import AnalyticsIcon from '@/assets/images/icons/AnalyticsIcon'
import BlocksIcon from '@/assets/images/icons/BlocksIcon'
import ChannelsIcon from '@/assets/images/icons/ChannelsIcon'
import EventsIcon from '@/assets/images/icons/EventsIcon'
import AnswersIcon from '@/assets/images/icons/AnswersIcon'
import ToolsIcon from '@/assets/images/icons/ToolsIcon'
import HistoryIcon from '@/assets/images/icons/HistoryIcon'
import SupportIcon from '@/assets/images/icons/SupportIcon.vue'
import SettingsIcon from '@/assets/images/icons/SettingsIcon.vue'
import LogoutIcon from '@/assets/images/icons/LogoutIcon.vue'
import AdminIcon from '@/assets/images/icons/AdminIcon.vue'
import BugReportDialog from '@/components/common/BugReportDialog.vue'
import UserProfileService from '@/services/amio/user-profile.service'
import PlainChatService from '@/services/plain-chat.service'
import utilsService from '@/services/amio/utils.service'
import toaster from '@/utils/toaster'
import { ROUTE_NAMES } from '@/router'
import amioLogo from '@/assets/images/logo/amio.svg'
import botModule from '@/store/modules/bot.module'
import templateModule from '@/store/modules/template.module'
import templateService from '@/services/amio/template.service'
import isEmpty from 'ramda/src/isEmpty'
import stringUtils from '@/utils/string.utils'
import { getLogicalBotId } from '@/utils/market.utils'
import { resolveLastOpenedBot } from '@/utils/last-opened-bot'
import { ITEM_IDS } from '@/utils/editor.constants'

export default {
  name: 'AmioSidebar',

  components: {
    ToolsIcon,
    HistoryIcon,
    EventsIcon,
    AnalyticsIcon,
    ChannelsIcon,
    BlocksIcon,
    AnswersIcon,
    SupportIcon,
    SettingsIcon,
    LogoutIcon,
    AdminIcon,
    BugReportDialog
  },

  props: {
    isDeactivated: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      ROUTE_NAMES,
      amioLogo,
      selectedItemIndex: 0,
      isBugReportShown: false,
      isSupportMenuOpen: false,
      supportMenuStyle: {},
      supportUnread: false,
      chatOpen: false,
      latestAgentEntryId: null,
      acknowledgedEntryId: null,
      notifiedEntryId: null
    }
  },

  computed: {
    isBotLoading: botModule.getBotLoading,
    botList: botModule.getBotList,
    bot: botModule.getBot,
    isSettingsRoute() {
      return this.$route.path.includes(ROUTE_NAMES.settings)
    },
    // The bot Answers/Events/Blocks should open. On Settings the route bot is the settings
    // selection, so use the last opened variant instead. Elsewhere the route bot wins.
    navBot() {
      if (this.isSettingsRoute) return resolveLastOpenedBot(this.botList)

      const routeBotId = this.$route.params.botId
      if (routeBotId) return this.botList.find(bot => bot.id === routeBotId) || null

      return this.bot
    },
    queryBots() {
      // Preserve current bot set encoded in the URL (needed for reload/state consistency).
      if (typeof this.$route.query.bots !== 'undefined') return this.$route.query.bots
      const logicalBotId = getLogicalBotId(this.navBot || this.bot)
      return logicalBotId || null
    },
    botId() {
      if (this.navBot) return this.navBot.id

      const routeBotId = this.$route.params.botId
      return routeBotId || botModule.getPrimaryBotId(routeBotId) || this.bot?.id
    },

    isEmptyBotList() {
      return isEmpty(this.botList)
    },

    isAdmin() {
      return UserProfileService.isAdmin()
    },

    templateList: templateModule.templateList,

    navigationItems() {
      const items = [
        {
          name: ROUTE_NAMES.answers,
          icon: 'answers-icon'
        },
        {
          name: ROUTE_NAMES.events,
          icon: 'events-icon'
        }
      ]
      if (!isEmpty(this.templateList)) {
        items.push({
          name: ROUTE_NAMES.blocks,
          icon: 'blocks-icon'
        })
      }
      items.push(
        {
          name: ROUTE_NAMES.tools,
          icon: 'tools-icon'
        },
        {
          name: ROUTE_NAMES.analytics,
          icon: 'analytics-icon'
        },
        {
          name: ROUTE_NAMES.history,
          icon: 'history-icon'
        }
      )
      return items
    }
  },

  created() {
    document.addEventListener('click', this.closeSupportMenu)
    window.addEventListener('resize', this.closeSupportMenu)
    window.addEventListener('scroll', this.closeSupportMenu, true)
  },

  mounted() {
    this.startSupportUnreadPolling()
    PlainChatService.prepare().then(() => {
      if (this._isDestroyed || this._isBeingDestroyed) return
      this.listenForPlainChat()
    }).catch(() => {
      // The next Chat click retries initialization.
    })
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeSupportMenu)
    window.removeEventListener('resize', this.closeSupportMenu)
    window.removeEventListener('scroll', this.closeSupportMenu, true)
    this.stopSupportUnreadPolling()
    clearTimeout(this._supportUnreadAfterClose)
    if (this._unsubscribePlainOpen) this._unsubscribePlainOpen()
    if (this._unsubscribePlainClose) this._unsubscribePlainClose()
  },

  methods: {
    isDisabled(name) {
      if (this.isDeactivated) return true
      // Tools is org-scoped (see resolveNavTo below) — unlike every other item here, it needs no bot
      // to exist, so an empty bot list must not disable it.
      if (name === ROUTE_NAMES.tools) return false
      return this.isEmptyBotList
    },

    resolveTooltipText(name) {
      if (name === ROUTE_NAMES.tools) return ''
      return `${stringUtils.capitalizeFirstChar(name)} will be available after a bot has been created`
    },

    resolveNavTo(name) {
      // Tools are org-scoped, so the route carries no botId.
      if (name === ROUTE_NAMES.tools) return { name }
      if (name === ROUTE_NAMES.analytics || name === ROUTE_NAMES.history) {
        return { name, query: this.queryBots !== null ? { bots: this.queryBots } : {} }
      }
      return { name, params: { botId: this.botId } }
    },

    linkClasses(i, name) {
      const applyTopBorder = i !== 0 && this.selectedItemIndex !== i && this.selectedItemIndex + 1 !== i
      const classes = [
        { 'border-solid border-0 border-t border-soft': applyTopBorder },
        { 'router-link-active': this.selectedItemIndex === i }
      ]

      if (this.isDisabled(name)) {
        classes.push('cursor-not-allowed text-gray hover:text-gray')
        return classes
      }

      if (this.isBotLoading) {
        classes.push('cursor-loading')
      } else {
        classes.push('cursor-pointer')
      }

      return classes
    },

    toggleSupportMenu() {
      this.isSupportMenuOpen = !this.isSupportMenuOpen
      if (this.isSupportMenuOpen) {
        this.allowSupportNotifications()
        this.$nextTick(this.updateSupportMenuPosition)
      }
    },

    closeSupportMenu() {
      this.isSupportMenuOpen = false
    },

    updateSupportMenuPosition() {
      const button = this.$refs.supportButton
      if (!button) return
      const rect = button.getBoundingClientRect()
      this.supportMenuStyle = {
        bottom: `${window.innerHeight - rect.bottom}px`,
        left: `${rect.right + 8}px`
      }
    },

    openChat() {
      this.closeSupportMenu()
      return PlainChatService.open().catch(err => {
        toaster.error('Could not open chat.', err)
      })
    },

    openBugReport() {
      this.closeSupportMenu()
      this.isBugReportShown = true
    },

    startSupportUnreadPolling() {
      this.refreshSupportUnread()
      // Keep polling in a background tab so an OS notification can fire while the chat is closed.
      this._supportUnreadTimer = setInterval(this.refreshSupportUnread, 60 * 1000)
      document.addEventListener('visibilitychange', this.onSupportVisibilityChange)
    },

    stopSupportUnreadPolling() {
      clearInterval(this._supportUnreadTimer)
      document.removeEventListener('visibilitychange', this.onSupportVisibilityChange)
    },

    onSupportVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.refreshSupportUnread()
        return
      }
      this.maybeNotifySupportReply(this.latestAgentEntryId)
    },

    async refreshSupportUnread() {
      try {
        const status = await utilsService.getPlainChatUnread()
        if (this._isDestroyed || this._isBeingDestroyed) return
        this.applySupportUnread(status)
      } catch (err) {
        // A failed poll keeps the current badge. The next visible poll retries.
      }
    },

    applySupportUnread(status) {
      const entryId = status?.entry_id || null
      this.latestAgentEntryId = entryId
      // Only swallow replies the customer could have seen in the open widget. A sticky
      // "ack the next id" flag would hide an agent reply that arrived after close.
      if (this.chatOpen && entryId) this.acknowledgedEntryId = entryId
      const unread = Boolean(status?.unread) && entryId !== this.acknowledgedEntryId
      this.supportUnread = unread && !this.chatOpen
      if (unread) this.maybeNotifySupportReply(entryId)
    },

    acknowledgeSupportUnread() {
      this.chatOpen = true
      this.supportUnread = false
      if (this.latestAgentEntryId) this.acknowledgedEntryId = this.latestAgentEntryId
    },

    listenForPlainChat() {
      const plain = window.Plain
      if (!plain) return
      if (typeof plain.onOpen === 'function') {
        this._unsubscribePlainOpen = plain.onOpen(() => {
          this.acknowledgeSupportUnread()
        })
      }
      if (typeof plain.onClose === 'function') {
        this._unsubscribePlainClose = plain.onClose(() => {
          this.chatOpen = false
          clearTimeout(this._supportUnreadAfterClose)
          // Give Plain a moment to record the customer's last message so closing right
          // after send does not look like an unread agent reply.
          this._supportUnreadAfterClose = setTimeout(() => {
            this.refreshSupportUnread()
          }, 1500)
        })
      }
    },

    allowSupportNotifications() {
      if (typeof Notification === 'undefined') return
      if (Notification.permission !== 'default') return
      const request = Notification.requestPermission()
      if (request && typeof request.catch === 'function') request.catch(() => {})
    },

    maybeNotifySupportReply(entryId) {
      if (!entryId || entryId === this.notifiedEntryId) return
      if (!this.supportUnread || this.chatOpen) return
      if (document.visibilityState !== 'hidden') return
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
      this.notifiedEntryId = entryId
      const notification = new Notification('New support reply', {
        body: 'You have a new message from support.'
      })
      notification.onclick = () => {
        window.focus()
        notification.close()
        this.openChat()
      }
    },

    settingsLinkClasses() {
      const classes = []
      if (this.isDeactivated) {
        classes.push('cursor-not-allowed text-gray hover:text-gray')
        return classes
      }

      if (this.isBotLoading) {
        classes.push('cursor-loading')
      } else {
        classes.push('cursor-pointer')
      }
      return classes
    },

    async loadTemplateList(botId) {
      templateModule.setTemplateList([])
      if (!botId || botId === ITEM_IDS.NEW) return
      try {
        const templates = await templateService.list(botId)
        if (botId !== this.botId) return
        templateModule.setTemplateList(templates)
      } catch (err) {
        if (botId !== this.botId) return
        toaster.error(err)
      }
    }
  },

  watch: {
    '$route.path': {
      immediate: true,
      handler(path) {
        this.selectedItemIndex = this.navigationItems.findIndex(item => path.includes(item.name))
      }
    },
    navigationItems() {
      this.selectedItemIndex = this.navigationItems.findIndex(item => this.$route.path.includes(item.name))
    },
    botId: {
      immediate: true,
      handler(botId) {
        this.loadTemplateList(botId)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.amio-logo {
  height: 100px;
  @apply flex justify-center items-center;
}

.sidebar {
  width: 100px;
  @apply h-full fixed top-0 left-0 bg-white flex flex-col items-center justify-between text-center px-3 z-20;

  &-item {
    @apply flex flex-col justify-center items-center capitalize w-23 h-23 gap-2 text-gray-dark;
    @apply hover:text-black hover:opacity-100 text-sm font-medium;
  }
}

.router-link-active {
  background-color: theme('colors.primary-light');
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
  @apply rounded w-23 h-23 text-primary hover:text-primary opacity-100;
}

.support-menu {
  @apply fixed z-50 flex flex-col bg-white rounded shadow-md py-1 border border-solid border-gray;
  min-width: 160px;

  &-item {
    @apply w-full text-left text-sm font-medium text-gray-dark bg-transparent border-0 cursor-pointer px-4 py-2;
    @apply hover:bg-primary-light hover:text-primary;
  }
}

.support-icon {
  @apply relative inline-flex;
}

.support-menu-label {
  @apply inline-flex items-center gap-2;
}

.support-unread-dot {
  width: 8px;
  height: 8px;
  @apply bg-primary rounded-full;
}

.support-icon .support-unread-dot {
  @apply absolute;
  top: -2px;
  right: -4px;
}
</style>
